/* GASSISA
========================================================================================
* Descripción: Módulo de Centro de Costos - Gestión de precios definitivos de artículos
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 13/05/2026 |  José Luis Aragón Cervantes       | Creación
*      2       | 08/06/2026 |  José Luis Aragón Cervantes       | Aumento de fuente en lista y banner de formulación
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { Fragment, useEffect, useMemo, useCallback, useRef, useState } from "react";
import { Box, Button, Typography, Divider } from "@mui/material";

import SizeSoftXLayout from "../leyout/SizeSoftXLayout";
import SizeSoftXTableVentas from "../leyout/SizeSoftXTableVentas";
import SizeSoftXModal from "../leyout/SizeSoftXModal";
import MnuCentroCostosActions from "./MnuCentroCostosActions";
import TenantSyncModal from "../tenantSync/TenantSyncModal";
import ModalAplicarOferta from "./ModalAplicarOferta";

import { useLayout } from "../../context/System/LayoutContext";
import { useSesion } from "../../context/System/SesionContext";
import { useRequest } from "../../context/System/RequestContext";
import { useFormTabLock } from "../../hooks/useFormTabLock";
import { useNavigate } from "react-router-dom";


import {
    apiCentroCostosCorridas,
    apiCentroCostosArticulos,
    apiCentroCostosActualizarCostos,
    apiCentroCostosAplicarOferta,
    apiCentroCostosEliminarOfertaFechas
} from "../../settings/ApiConfig";

const initialFormulario = {
    formula: "",
    preciosugerido: 0,
    preciodefinitivo: 0,
    pmvb: 0
};

const STORAGE_VERSION = 'v1';

const getStoredFormulario = (key) => {
    try {
        const stored = localStorage.getItem(key);
        if (!stored) return initialFormulario;
        const parsed = JSON.parse(stored);
        if (parsed.version !== STORAGE_VERSION) return initialFormulario;
        return parsed.data;
    } catch {
        return initialFormulario;
    }
};

function FormulaInputCell({ row, onSave, onNavigate, disabled }) {
  const [valor, setValor] = useState(row.sFormulario || "");
  const inputRef = useRef(null);

  useEffect(() => {
    setValor(row.sFormulario || "");
  }, [row.sFormulario, row.id]);

  const calcularPrecioSugerido = (formula, CUC, pmvb) => {
    try {
      let expr = formula
        .replace(/costounitario/gi, String(CUC || 0))
        .replace(/cuc/gi, String(CUC || 0))
        .replace(/pmvb/gi, String(pmvb || 0));

      if (/^[+-]?\d+(\.\d+)?$/.test(expr.trim())) {
        const margin = parseFloat(expr) || 0;
        return CUC * (1 + margin / 100);
      }

      const resultado = parseFloat(eval(expr));
      return isNaN(resultado) ? 0 : resultado;
    } catch (err) {
      return 0;
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (valor !== row.sFormulario) {
        const nuevoPrecioSugerido = calcularPrecioSugerido(valor, row.dCUC, row.dPMVB);
        await onSave(row, { sFormulario: valor, dPrecioSugerido: nuevoPrecioSugerido });
      }
      onNavigate(row, e);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setValor(row.sFormulario || "");
      return;
    }
  };

  if (disabled) {
    return (
      <TextField autoComplete="off"
        size="small"
        value={row.sFormulario || ""}
        disabled
        inputProps={{ style: { textAlign: "center", padding: "4px 2px", width: "100px" } }}
      />
    );
  }

  return (
    <TextField autoComplete="off"
      size="small"
      inputRef={inputRef}
      value={valor}
      onChange={(e) => setValor(e.target.value)}
      onClick={(e) => {
        if (e.target && e.target.select) e.target.select();
      }}
      onFocus={(e) => {
        setValor(row.sFormulario || "");
        setTimeout(() => {
          if (inputRef.current && inputRef.current.select) {
            inputRef.current.select();
          }
        }, 0);
      }}
      onKeyDown={handleKeyDown}
      inputProps={{
        "data-formula-id": row.id,
        style: { textAlign: "center", padding: "4px 2px", width: "100px" }
      }}
    />
  );
}

function CostoCell({ row, onSave, onNavigate, disabled, tipo }) {
  const valorCampo = tipo === "pmvb" ? row.dPMVB : row.dPrecioDefinitivo;
  const [valor, setValor] = useState(String(valorCampo || 0));
  const inputRef = useRef(null);

  useEffect(() => {
    setValor(String(valorCampo || 0));
  }, [valorCampo, row.id]);

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
      const nuevoValor = parseFloat(valor);
      if (valor !== "" && !isNaN(nuevoValor) && nuevoValor >= 0 && nuevoValor !== valorCampo) {
        await onSave(row, { [tipo === "pmvb" ? "dPMVB" : "dPrecioDefinitivo"]: nuevoValor });
      }
      onNavigate(row, e);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setValor(String(valorCampo || 0));
      return;
    }
  };

  if (disabled) {
    return (
      <TextField autoComplete="off"
        size="small"
        value={valorCampo || 0}
        disabled
        inputProps={{ style: { textAlign: "right", padding: "4px 2px", width: "80px" } }}
      />
    );
  }

  return (
    <TextField autoComplete="off"
      size="small"
      inputRef={inputRef}
      value={valor}
      onClick={(e) => {
        if (e.target && e.target.select) e.target.select();
      }}
      onFocus={(e) => {
        setValor(String(valorCampo || 0));
        setTimeout(() => {
          if (inputRef.current && inputRef.current.select) {
            inputRef.current.select();
          }
        }, 0);
      }}
      onChange={(e) => setValor(e.target.value)}
      onKeyDown={handleKeyDown}
      inputProps={{
        min: 0,
        inputMode: "decimal",
        "data-pmvb-id": tipo === "pmvb" ? row.id : undefined,
        "data-preciodefinitivo-id": tipo === "preciodefinitivo" ? row.id : undefined,
        style: { textAlign: "right", padding: "4px 2px", width: "80px" },
      }}
    />
  );
}

export default function CentroCostosGeneral() {
    const navigate = useNavigate();
    const handleClickRegresarMainDashboard = useCallback(() => navigate("/*"), [navigate]);

    const { isLocked: isFormLocked } = useFormTabLock('centro_costos', {
        title: 'Centro de Costos',
        onLockDetected: (info) => {
            console.warn(`El formulario de ${info?.title || 'Centro de Costos'} ya está abierto en otra pestaña`);
        }
    });

    const { PostRequest, GetRequest } = useRequest();

    const {
        handleChangeTitle,
        handleChangeClavePaginaActual,
        handleOpenAlert
    } = useLayout();

    const { parametrosCajaForm } = useSesion();

    const [corridas, setCorridas] = useState([]);

    const [corridaSeleccionada, setCorridaSeleccionada] =
        useState(null);

    const [corridaInputValue, setCorridaInputValue] =
        useState("");

    const [descripcionFiltro, setDescripcionFiltro] =
        useState("");

    const [listaArticulos, setListaArticulos] =
        useState([]);

    const [listaOriginal, setListaOriginal] =
        useState([]);

    const [filteredArticulos, setFilteredArticulos] =
        useState([]);

    const [columnFilters, setColumnFilters] =
        useState({});

    const [formularioBanner, setFormularioBanner] =
        useState(() => getStoredFormulario('centrocostos_formulario'));

    const [loading, setLoading] =
        useState(false);

    const [selectedRow, setSelectedRow] =
        useState(null);

    const [openModalOferta, setOpenModalOferta] =
        useState(false);

    const [openConfirmAplicarModal, setOpenConfirmAplicarModal] =
        useState(false);
    const [openTenantSync, setOpenTenantSync] = useState(false);
    const [pendingAplicarRequest, setPendingAplicarRequest] =
        useState(null);

    const [ofertaScope, setOfertaScope] =
        useState({
            articulos: []
        });

    const formulaRef = useRef(null);
    const pmvbRef = useRef(null);
    const precioDefinitivoRef = useRef(null);

    const hayFiltrosActivos = useMemo(() => {
        return filteredArticulos.length > 0 &&
               filteredArticulos.length < listaArticulos.length;

    }, [filteredArticulos, listaArticulos]);

    const hayFiltrosActivosRef = useRef(hayFiltrosActivos);
    const filteredArticulosRef = useRef(filteredArticulos);

    useEffect(() => {
        hayFiltrosActivosRef.current = hayFiltrosActivos;
    }, [hayFiltrosActivos]);

    useEffect(() => {
        filteredArticulosRef.current = filteredArticulos;
    }, [filteredArticulos]);

    const EDITABLE_FIELDS = useMemo(() => ["formula", "preciodefinitivo", "pmvb"], []);

    const handleGridNavigation = useCallback((currentRow, currentField, event) => {
        const listSource = hayFiltrosActivosRef.current ? filteredArticulosRef.current : listaArticulos;
        if (!listSource || listSource.length === 0) return;
        const rowIndex = listSource.findIndex((r) => r.id === currentRow.id);
        if (rowIndex === -1) return;

        let targetRowIndex = rowIndex;
        let targetField = currentField;

        const fieldIndex = EDITABLE_FIELDS.indexOf(currentField);

        if (event.key === "ArrowDown" || (event.key === "Enter" && !event.shiftKey)) {
            event.preventDefault();
            event.stopPropagation();
            targetRowIndex = rowIndex + 1;
            if (targetRowIndex >= listSource.length) targetRowIndex = 0;
        } else if (event.key === "ArrowUp" || (event.key === "Enter" && event.shiftKey)) {
            event.preventDefault();
            event.stopPropagation();
            targetRowIndex = rowIndex - 1;
            if (targetRowIndex < 0) targetRowIndex = listSource.length - 1;
        } else if (event.key === "Tab") {
            event.preventDefault();
            event.stopPropagation();
            if (event.shiftKey) {
                if (fieldIndex > 0) {
                    targetField = EDITABLE_FIELDS[fieldIndex - 1];
                } else {
                    targetRowIndex = rowIndex - 1;
                    if (targetRowIndex < 0) targetRowIndex = listSource.length - 1;
                    targetField = EDITABLE_FIELDS[EDITABLE_FIELDS.length - 1];
                }
            } else {
                if (fieldIndex < EDITABLE_FIELDS.length - 1) {
                    targetField = EDITABLE_FIELDS[fieldIndex + 1];
                } else {
                    targetRowIndex = rowIndex + 1;
                    if (targetRowIndex >= listSource.length) targetRowIndex = 0;
                    targetField = EDITABLE_FIELDS[0];
                }
            }
        } else {
            return;
        }

        const nextRow = listSource[targetRowIndex];
        if (nextRow) {
            setTimeout(() => {
                const selector = `[data-${targetField}-id="${nextRow.id}"]`;
                const targetInput = document.querySelector(selector);
                if (targetInput) {
                    targetInput.focus();
                    if (targetInput.select) targetInput.select();
                }
            }, 30);
        }
    }, [listaArticulos, EDITABLE_FIELDS]);

    const handleActualizarFilaIndividual = useCallback(async (row, updates) => {
        const previousList = [...listaArticulos];

        const nuevaLista = listaArticulos.map(art => {
            if (art.id === row.id) {
                const calculatedFields = {};
                if (updates.sFormulario !== undefined) {
                    calculatedFields.sFormulario = updates.sFormulario;
                }
                if (updates.dPrecioSugerido !== undefined) {
                    calculatedFields.dPrecioSugerido = updates.dPrecioSugerido;
                }
                if (updates.dPrecioDefinitivo !== undefined) {
                    calculatedFields.dPrecioDefinitivo = updates.dPrecioDefinitivo;
                }
                if (updates.dPMVB !== undefined) {
                    calculatedFields.dPMVB = updates.dPMVB;
                }
                return {
                    ...art,
                    ...calculatedFields
                };
            }
            return art;
        });

        setListaArticulos(nuevaLista);

        try {
            const request = {
                sUsuario: parametrosCajaForm.usuario || "SISTEMA",
                sFormula: updates.sFormulario !== undefined ? updates.sFormulario : row.sFormulario,
                dPrecioSugerido: updates.dPrecioSugerido !== undefined ? Number(updates.dPrecioSugerido) : Number(row.dPrecioSugerido),
                dPrecioDefinitivo: updates.dPrecioDefinitivo !== undefined ? Number(updates.dPrecioDefinitivo) : Number(row.dPrecioDefinitivo),
                dPMVB: updates.dPMVB !== undefined ? Number(updates.dPMVB) : Number(row.dPMVB),
                listaArticulos: [
                    {
                        sClaveArticulo: row.sClaveArticulo,
                        dMedida: Number(row.dMedida)
                    }
                ]
            };

            const result = await PostRequest({
                url: apiCentroCostosActualizarCostos,
                body: request
            });

            if (result.hasError) {
                handleOpenAlert(
                    "Error al actualizar costos: " + result.message,
                    "error"
                );
                setListaArticulos(previousList);
            }
        } catch (error) {
            handleOpenAlert(
                "Error de red al actualizar costos: " + error.message,
                "error"
            );
            setListaArticulos(previousList);
        }
    }, [listaArticulos, parametrosCajaForm, handleOpenAlert]);

    const handleOpenModalOferta = () => {

        const source =
            hayFiltrosActivos
                ? filteredArticulos
                : listaArticulos;

        if (source.length === 0) {

            handleOpenAlert(
                "No hay artículos para gestionar oferta",
                "warning"
            );

            return;
        }

        setOfertaScope({
            articulos: [...source]
        });

        setOpenModalOferta(true);
    };

    const handleChangeFormulario = (e) => {

        const { name, value } = e.target;

        setFormularioBanner(prev => {

            let nuevoEstado = {
                ...prev,
                [name]: value
            };

            if (name === "formula") {

                try {

                    const resultado =
                        parseFloat(eval(value));

                    nuevoEstado.preciosugerido =
                        !isNaN(resultado) &&
                        isFinite(resultado)
                            ? resultado
                            : 0;

                } catch {

                    nuevoEstado.preciosugerido = 0;
                }
            }

            return nuevoEstado;
        });
    };

    const handleKeyDownFormula = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();

            try {

                const resultado =
                    parseFloat(
                        eval(formularioBanner.formula)
                    );

                if (
                    !isNaN(resultado) &&
                    isFinite(resultado)
                ) {

                    setFormularioBanner(prev => ({
                        ...prev,
                        preciosugerido: resultado
                    }));
                }

            } catch (err) {

                handleOpenAlert(
                    "Fórmula invélida: " + err.message,
                    "warning"
                );
            }

            setTimeout(() => {
                precioDefinitivoRef.current?.focus();
                precioDefinitivoRef.current?.select();
            }, 50);
        }
    };

    const handleKeyDownPrecioDefinitivo = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();

            setTimeout(() => {
                pmvbRef.current?.focus();
                pmvbRef.current?.select();
            }, 50);
        }
    };

    const handleCalcularPrecios = () => {

        if (!formularioBanner.formula.trim()) {

            handleOpenAlert(
                "Ingrese una fórmula de célculo",
                "warning"
            );

            return;
        }

        const precioSugerido =
            Number(
                formularioBanner.preciosugerido
            ) || 0;

        const pmvb =
            Number(
                formularioBanner.pmvb
            ) || 0;

        const source =
            hayFiltrosActivos
                ? filteredArticulos
                : listaArticulos;

        if (source.length === 0) {

            handleOpenAlert(
                "No hay artículos visibles",
                "warning"
            );

            return;
        }

        const visiblesKeys = new Set(
            source.map(a =>
                `${a.sClaveArticulo}-${a.dMedida}`
            )
        );

        const nuevaLista = listaArticulos.map(art => {

            const key =
                `${art.sClaveArticulo}-${art.dMedida}`;

            if (!visiblesKeys.has(key)) {
                return art;
            }

            let nuevoPrecio =
                art.dPrecioDefinitivo;

            try {

                const formula =
                    formularioBanner.formula
                        .replace(
                            /PRECIOSUGERIDO/g,
                            precioSugerido.toString()
                        )
                        .replace(
                            /PMVB/g,
                            pmvb.toString()
                        )
                        .replace(
                            /CUC/g,
                            Number(art.dCUC).toString()
                        )
                        .replace(
                            /PRECIODEFINITIVO/g,
                            Number(
                                art.dPrecioDefinitivo
                            ).toString()
                        );

                nuevoPrecio =
                    parseFloat(eval(formula));

                if (
                    isNaN(nuevoPrecio) ||
                    !isFinite(nuevoPrecio)
                ) {
                    nuevoPrecio =
                        art.dPrecioDefinitivo;
                }

            } catch {

                nuevoPrecio =
                    art.dPrecioDefinitivo;
            }

            return {
                ...art,
                dPrecioDefinitivo: nuevoPrecio
            };
        });

        setListaArticulos(nuevaLista);

        handleOpenAlert(
            "Precios calculados correctamente",
            "ok"
        );
    };

    const handleRowClick = useCallback((dataRow) => {
        setSelectedRow(dataRow);
        if (dataRow) {
            setFormularioBanner({
                formula: dataRow.sFormulario || "",
                preciosugerido: dataRow.dPrecioSugerido || 0,
                preciodefinitivo: dataRow.dPrecioDefinitivo || 0,
                pmvb: dataRow.dPMVB || 0
            });
        }
    }, []);

    const handleDeshacerCambios = () => {

        setListaArticulos([
            ...listaOriginal
        ]);

        setFormularioBanner(
            initialFormulario
        );
    };

    const handleConfirmarAplicar = async () => {
        if (!pendingAplicarRequest) return;

        setLoading(true);

        try {
            const result = await PostRequest({
                url: apiCentroCostosActualizarCostos,
                body: pendingAplicarRequest
            });

            setOpenConfirmAplicarModal(false);
            setPendingAplicarRequest(null);

            if (result.hasError) {
                handleOpenAlert(
                    "Error al actualizar: " +
                    result.message,
                    "error"
                );
                return;
            }

            handleOpenAlert(
                `${result.result.iArticulosActualizados} artículos actualizados`,
                "ok"
            );

            handleBuscarArticulos();
        } catch (error) {
            handleOpenAlert(
                "Error al actualizar costos: " +
                error.message,
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleActualizarCostos = async () => {

        if (listaArticulos.length === 0) {

            handleOpenAlert(
                "No hay artículos cargados",
                "warning"
            );

            return;
        }

        const source =
            hayFiltrosActivos
                ? filteredArticulos
                : listaArticulos;

        if (source.length === 0) {

            handleOpenAlert(
                "No hay artículos para actualizar",
                "warning"
            );

            return;
        }

        const request = {
            sUsuario:
                parametrosCajaForm.usuario,

            sFormula:
                formularioBanner.formula,

            dPrecioSugerido:
                Number(
                    formularioBanner.preciosugerido
                ) || 0,

            dPMVB:
                Number(
                    formularioBanner.pmvb
                ) || 0,

            dPrecioDefinitivo:
                Number(
                    formularioBanner.preciodefinitivo
                ) || 0,

            listaArticulos:
                source.map(art => ({
                    sClaveArticulo:
                        art.sClaveArticulo,
                    dMedida:
                        Number(art.dMedida),
                    sFormulario:
                        art.sFormulario || "",
                    dPrecioDefinitivo:
                        Number(art.dPrecioDefinitivo) || 0,
                    dPMVB:
                        Number(art.dPMVB) || 0
                }))
        };

        setPendingAplicarRequest(request);
        setOpenConfirmAplicarModal(true);
    };

    const handleBuscarArticulos = async (
        corridaValue
    ) => {

        setLoading(true);

        try {

            const corrida =
                corridaValue !== undefined
                    ? corridaValue
                    : corridaInputValue.trim() === ""
                        ? null
                        : corridaSeleccionada;

            const filtro = {

                sCorrida:
                    corrida?.sCorrida || null,

                sDescripcion:
                    descripcionFiltro.trim() ||
                    null,

                sStatus: "ACTIVO"
            };

            const data = await PostRequest({
                url: apiCentroCostosArticulos,
                body: filtro
            });

            if (data.hasError) {

                handleOpenAlert(
                    "Error al buscar artículos: " +
                    data.message,
                    "error"
                );

                return;
            }

            const articulosConId = (data.result || []).map(a => ({
                ...a,
                id: `${a.sClaveArticulo}-${a.dMedida}`
            }));

            setListaArticulos(articulosConId);

            setListaOriginal(articulosConId);

            setFilteredArticulos([]);

            setColumnFilters({});

        } catch {

            handleOpenAlert(
                "Error al buscar artículos",
                "error"
            );

        } finally {

            setLoading(false);
        }
    };

    const handleAplicarOferta = async (
        ofertaData
    ) => {

        const source =
            ofertaScope.articulos;

        if (source.length === 0) {

            handleOpenAlert(
                "No hay artículos para aplicar oferta",
                "warning"
            );

            return;
        }

        setLoading(true);

        try {

            const request = {

                sUsuario:
                    parametrosCajaForm.usuario,

                dtFechaInicio:
                    ofertaData.dtFechaInicio,

                dtFechaFin:
                    ofertaData.dtFechaFin,

                dCostoOferta:
                    Number(
                        ofertaData.dCostoOferta
                    ) || 0,

                listaArticulos:
                    source
                        .filter(art => {

                            const precioRegular =
                                Number(
                                    art.dPrecioDefinitivo
                                ) || 0;

                            const costoOferta =
                                Number(
                                    ofertaData
                                        .dCostoOferta
                                ) || 0;

                            const porcentaje =
                                costoOferta > 0 &&
                                precioRegular > 0
                                    ? (precioRegular -
                                        costoOferta) /
                                    costoOferta
                                    : 0;

                            return porcentaje > 0;
                        })
                        .map(art => {

                            const precioRegular =
                                Number(
                                    art.dPrecioDefinitivo
                                ) || 0;

                            const costoOferta =
                                Number(
                                    ofertaData
                                        .dCostoOferta
                                ) || 0;

                            const porcentajeAplicable =
                                (precioRegular -
                                    costoOferta) /
                                costoOferta;

                            return {

                                sClaveArticulo:
                                    art.sClaveArticulo,

                                dMedida:
                                    Number(art.dMedida),

                                dCantidadAplicable:
                                    Number(
                                        ofertaData
                                            .dCantidadAplicable
                                    ) || 1,

                                dPorcentajeAplicable:
                                    Math.round(
                                        porcentajeAplicable *
                                        10000
                                    ) / 10000
                            };
                        })
            };

            const result = await PostRequest({
                url: apiCentroCostosAplicarOferta,
                body: request
            });

            if (result.hasError) {

                handleOpenAlert(
                    "Error al aplicar oferta: " +
                    result.message,
                    "error"
                );

                return;
            }

            handleOpenAlert(
                `${result.result.iArticulosActualizados} artículos con oferta aplicada`,
                "ok"
            );

            setOpenModalOferta(false);

            handleBuscarArticulos();

        } catch (error) {

            handleOpenAlert(
                "Error al aplicar oferta: " +
                error.message,
                "error"
            );

        } finally {

            setLoading(false);
        }
    };

    const handleEliminarOfertaPorFechas =
        async (fechasData) => {

        setLoading(true);

        try {

            const request = {

                sUsuario:
                    parametrosCajaForm.usuario,

                dtFechaInicio:
                    fechasData.dtFechaInicio,

                dtFechaFin:
                    fechasData.dtFechaFin
            };

            const result = await PostRequest({
                url: apiCentroCostosEliminarOfertaFechas,
                body: request
            });

            if (result.hasError) {

                handleOpenAlert(
                    "Error al eliminar ofertas: " +
                    result.message,
                    "error"
                );

                return;
            }

            handleOpenAlert(
                `${result.result.iArticulosActualizados} ofertas eliminadas por rango de fechas`,
                "ok"
            );

            setOpenModalOferta(false);

            handleBuscarArticulos();

        } catch (error) {

            handleOpenAlert(
                "Error al eliminar ofertas: " +
                error.message,
                "error"
            );

        } finally {

            setLoading(false);
        }
    };

    const hayArticulosConOferta =
        useMemo(() => {

        const source =
            hayFiltrosActivos
                ? filteredArticulos
                : listaArticulos;

        return source.some(
            art =>
                art.sStatusOferta ===
                "Oferta activa"
        );

    }, [
        filteredArticulos,
        listaArticulos,
        hayFiltrosActivos
    ]);

    useEffect(() => {

        handleChangeTitle(
            "Centro de Costos - Costos de Artículos"
        );

        handleChangeClavePaginaActual(
            "CentroCostos"
        );

        const cargarCorridas = async () => {
            const data = await GetRequest({
                url: apiCentroCostosCorridas
            });

            if (data.hasError) {
                handleOpenAlert(
                    "Error al cargar corridas: " + data.message,
                    "error"
                );
                return;
            }

            setCorridas(
                (data.result || []).map(c => ({
                    id: c.sCorrida,
                    sCorrida: c.sCorrida,
                    iCantidadArticulos: c.iCantidadArticulos
                }))
            );
        };

        cargarCorridas();

    }, [handleChangeTitle, handleChangeClavePaginaActual, handleOpenAlert]);

    useEffect(() => {
        localStorage.setItem('centrocostos_formulario', JSON.stringify({ version: STORAGE_VERSION, data: formularioBanner }));
    }, [formularioBanner]);

    useEffect(() => {
        if (isFormLocked) {
            handleOpenAlert("El formulario de Centro de Costos ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.", "error");
            setTimeout(() => {
                handleClickRegresarMainDashboard();
            }, 900);
        }
    }, [isFormLocked, handleOpenAlert, handleClickRegresarMainDashboard]);

    useEffect(() => {

        const handleKeyDown = (e) => {

            if (
                e.altKey &&
                e.key === "F1"
            ) {

                e.preventDefault();

                const descInput =
                    document.getElementById(
                        "descripcion-filtro"
                    );

                if (descInput) {

                    descInput.focus();
                    descInput.select();
                }
            }
        };

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {

            window.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };

    }, []);

    const columnas = useMemo(() => [

        {
            title: "Descripción",
            field: "sDescripcion"
        },

        {
            title: "Medida",
            field: "dMedida"
        },

        {
            title: "Fórmula",
            field: "sFormulario",
            render: (row) => (
                <FormulaInputCell
                    row={row}
                    disabled={loading}
                    onSave={handleActualizarFilaIndividual}
                    onNavigate={(r, e) => handleGridNavigation(r, "formula", e)}
                />
            )
        },

        {
            title: "Precio Sugerido",
            field: "dPrecioSugerido",
            format: (value) =>
                value?.toFixed(2) || "0.00"
        },

        {
            title: "Precio Definitivo",
            field: "dPrecioDefinitivo",
            render: (row) => (
                <CostoCell
                    row={row}
                    disabled={loading}
                    onSave={handleActualizarFilaIndividual}
                    onNavigate={(r, e) => handleGridNavigation(r, "preciodefinitivo", e)}
                    tipo="preciodefinitivo"
                />
            )
        },

        {
            title: "PMVB",
            field: "dPMVB",
            render: (row) => (
                <CostoCell
                    row={row}
                    disabled={loading}
                    onSave={handleActualizarFilaIndividual}
                    onNavigate={(r, e) => handleGridNavigation(r, "pmvb", e)}
                    tipo="pmvb"
                />
            )
        },

        {
            title: "CUC",
            field: "dCUC",
            format: (value) =>
                value?.toFixed(2) || "0.00"
        },

        {
            title: "Status Oferta",
            field: "sStatusOferta",
            render: (rowData) => {

                if (
                    rowData.sStatusOferta ===
                    "Oferta activa"
                ) {

                    return (
                        <span
                            style={{
                                color: "#1976d2",
                                fontWeight: "bold"
                            }}
                        >
                            Oferta activa
                        </span>
                    );
                }

                return null;
            }
        }

    ], [loading, handleActualizarFilaIndividual, handleGridNavigation]);

    return (

        <Fragment>

            <SizeSoftXLayout

                titlePage="Centro de Costos - Costos de Artículos"

                buttonActions={
                    <MnuCentroCostosActions
                        onRegresar={
                            handleClickRegresarMainDashboard
                        }
                        onActualizarCostos={
                            handleActualizarCostos
                        }
                        onDeshacerCambios={
                            handleDeshacerCambios
                        }
                        onOpenModalOferta={
                            handleOpenModalOferta
                        }
                        loading={loading}
                        listaArticulos={
                            listaArticulos
                        }
                        hayArticulosConOferta={
                            hayArticulosConOferta
                        }
                        onOpenTenantSync={() => setOpenTenantSync(true)}
                    />
                }
            >

                <Box sx={{ p: 2 }}>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            mb: 2,
                            alignItems: "center"
                        }}
                    >

                        <Autocomplete
                            freeSolo
                            clearOnEscape
                            options={corridas}
                            getOptionLabel={(option) => {

                                if (
                                    typeof option ===
                                    "string"
                                ) {
                                    return option;
                                }

                                return option?.sCorrida || "";
                            }}
                            value={
                                corridaSeleccionada
                            }
                            inputValue={
                                corridaInputValue
                            }
                            onInputChange={(
                                e,
                                newInputValue
                            ) => {

                                setCorridaInputValue(
                                    newInputValue
                                );

                                if (
                                    newInputValue.trim() === ""
                                ) {

                                    setCorridaSeleccionada(
                                        null
                                    );
                                }
                            }}
                            onChange={(
                                e,
                                newValue
                            ) => {

                                if (!newValue) {

                                    setCorridaSeleccionada(
                                        null
                                    );

                                    setCorridaInputValue(
                                        ""
                                    );

                                    return;
                                }

                                if (
                                    typeof newValue ===
                                    "string"
                                ) {

                                    const corridaEncontrada =
                                        corridas.find(
                                            c =>
                                                c.sCorrida ===
                                                newValue
                                        );

                                    setCorridaSeleccionada(
                                        corridaEncontrada ||
                                        null
                                    );

                                    setCorridaInputValue(
                                        newValue
                                    );

                                    return;
                                }

                                setCorridaSeleccionada(
                                    newValue
                                );

                                setCorridaInputValue(
                                    newValue?.sCorrida ||
                                    ""
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    autoComplete="off"
                                    {...params}
                                    label="Corrida"
                                    size="small"
                                    sx={{
                                        width: 250
                                    }}
                                    onKeyDown={(e) => {

                                        if (
                                            e.key ===
                                            "Enter"
                                        ) {

                                            e.preventDefault();

                                            if (
                                                descripcionFiltro.trim() === ""
                                            ) {
                                                return;
                                            }

                                            const corridaActual =
                                                corridaInputValue.trim() === ""
                                                    ? null
                                                    : corridaSeleccionada;

                                            handleBuscarArticulos(
                                                corridaActual
                                            );
                                        }
                                    }}
                                />
                            )}
                        />

                        <TextField autoComplete="off"
                            id="descripcion-filtro"
                            label="Descripción"
                            value={
                                descripcionFiltro
                            }
                            onChange={(e) =>
                                setDescripcionFiltro(
                                    e.target.value
                                )
                            }
                            onKeyDown={(e) => {

                                if (
                                    e.key === "Enter"
                                ) {

                                    e.preventDefault();

                                    handleBuscarArticulos();
                                }
                            }}
                            size="small"
                            sx={{ width: 300 }}
                        />

                        <Button
                            variant="contained"
                            onClick={() =>
                                handleBuscarArticulos()
                            }
                        >
                            Buscar
                        </Button>

                    </Box>

                    <Box sx={{
                        maxHeight: '72vh',
                        overflow: 'auto'
                    }}>
                        <SizeSoftXTableVentas
                            columns={columnas}
                            data={listaArticulos}
                            id="id"
                            fontSize="medium"
                            row={selectedRow}
                            setRow={handleRowClick}
                            showFilters={true}
                            disablePagination={true}
                            onFiltersChange={
                                setColumnFilters
                            }
                        />
                    </Box>

                </Box>

                <Box
                    sx={{
                        position: 'fixed',
                        bottom: '10px',
                        left: '20px',
                        right: '20px',
                        borderRadius: '12px',
                        zIndex: 1100,
                        backgroundColor: '#fff',
                        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        px: 3,
                        py: '8px',
                        borderTop: '1px solid #ddd',
                        transition: 'all 0.3s ease'
                    }}
                >

                    <TextField autoComplete="off"
                        inputRef={formulaRef}
                        name="formula"
                        label="Fórmula"
                        value={
                            formularioBanner.formula
                        }
                        onChange={
                            handleChangeFormulario
                        }
                        onKeyDown={
                            handleKeyDownFormula
                        }
                        size="small"
                        sx={{ width: '210px', '& .MuiInputBase-input': { fontSize: '0.95rem' }, '& .MuiInputLabel-root': { fontSize: '0.95rem' } }}
                    />

                    <TextField autoComplete="off"
                        name="preciosugerido"
                        label="Precio Sugerido"
                        value={
                            formularioBanner.preciosugerido
                        }
                        disabled
                        size="small"
                        sx={{ width: '120px', '& .MuiInputBase-input': { fontSize: '0.95rem' }, '& .MuiInputLabel-root': { fontSize: '0.95rem' } }}
                    />

                    <TextField autoComplete="off"
                        inputRef={
                            precioDefinitivoRef
                        }
                        name="preciodefinitivo"
                        label="Precio Definitivo"
                        value={
                            formularioBanner.preciodefinitivo
                        }
                        onChange={
                            handleChangeFormulario
                        }
                        onKeyDown={
                            handleKeyDownPrecioDefinitivo
                        }
                        size="small"
                        sx={{ width: '120px', '& .MuiInputBase-input': { fontSize: '0.95rem' }, '& .MuiInputLabel-root': { fontSize: '0.95rem' } }}
                    />

                    <TextField autoComplete="off"
                        inputRef={pmvbRef}
                        name="pmvb"
                        label="PMVB"
                        value={
                            formularioBanner.pmvb
                        }
                        onChange={
                            handleChangeFormulario
                        }
                        size="small"
                        sx={{ width: '120px', '& .MuiInputBase-input': { fontSize: '0.95rem' }, '& .MuiInputLabel-root': { fontSize: '0.95rem' } }}
                    />

                </Box>

                <ModalAplicarOferta
                    open={openModalOferta}
                    onClose={() =>
                        setOpenModalOferta(false)
                    }
                    onAplicar={
                        handleAplicarOferta
                    }
                    onEliminarPorFechas={
                        handleEliminarOfertaPorFechas
                    }
                    articulosOferta={
                        ofertaScope.articulos
                    }
                    totalListaOriginal={
                        listaArticulos.length
                    }
                    loading={loading}
                />

                <SizeSoftXModal
                    open={openConfirmAplicarModal}
                    title="Confirmar aplicación de costos"
                    onClose={() => {
                        setOpenConfirmAplicarModal(false);
                        setPendingAplicarRequest(null);
                    }}
                    onSuccess={handleConfirmarAplicar}
                    success="Aplicar"
                >
                    <Box sx={{ p: 2 }}>
                        <Typography
                            variant="subtitle2"
                        >
                            Valores a aplicar:
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography>
                            Fórmula:{" "}
                            {formularioBanner.formula ||
                                "(sin cambios)"}
                        </Typography>
                        <Typography>
                            Precio sugerido:{" "}
                            {formularioBanner.preciosugerido}
                        </Typography>
                        <Typography>
                            Precio definitivo:{" "}
                            {formularioBanner.preciodefinitivo}
                        </Typography>
                        <Typography>
                            PMVB:{" "}
                            {formularioBanner.pmvb}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography>
                            Artículos a afectar:{" "}
                            <strong>
                                {pendingAplicarRequest
                                    ?.listaArticulos
                                    ?.length || 0}
                            </strong>
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography
                            variant="caption"
                            color="textSecondary"
                        >
                            Primeros artículos:
                        </Typography>
                        {pendingAplicarRequest?.listaArticulos
                            ?.slice(0, 5)
                            .map((art, i) => (
                                <Typography
                                    key={i}
                                    variant="caption"
                                    display="block"
                                >
                                    {art.sClaveArticulo} -{" "}
                                    {art.dMedida}
                                </Typography>
                            ))}
                        {pendingAplicarRequest
                            ?.listaArticulos?.length > 5 && (
                            <Typography
                                variant="caption"
                                color="textSecondary"
                            >
                                ...y{" "}
                                {pendingAplicarRequest
                                    .listaArticulos.length -
                                    5}{" "}
                                més
                            </Typography>
                        )}
                    </Box>
                </SizeSoftXModal>

                <TenantSyncModal
                    open={openTenantSync}
                    onClose={() => setOpenTenantSync(false)}
                    syncType="COST_CENTERS"
                    itemKeys={(hayFiltrosActivos ? filteredArticulos : listaArticulos).map((item) => `${String(item.sClaveArticulo || "").trim()}|${item.dMedida}`)}
                    title="Sincronizar centro de costos"
                />

            </SizeSoftXLayout>

        </Fragment>
    );
}
